import { type FC } from 'react';
import { DragDropContext, Draggable, type DropResult } from 'react-beautiful-dnd';

import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import { type CourseFile, type SelectOption } from './types';
import FileRow from './FileRow';

interface Props {
	courseId: number;
	files: CourseFile[];
	selectOptions: SelectOption[];
	/** Set of file IDs that should start expanded (e.g. newly created). */
	expandedIds?: Set<string>;
	onChange: (updated: CourseFile) => void;
	onRemove: (id: string) => void;
	onReplace: (id: string) => void;
	onReorder: (result: DropResult) => void;
}

const FilesTable: FC<Props> = ({
	courseId,
	files,
	selectOptions,
	expandedIds,
	onChange,
	onRemove,
	onReplace,
	onReorder
}) => (
	<DragDropContext onDragEnd={onReorder}>
		<table className="filesTable">
			<thead>
				<tr>
					<th className="dragHandleHeader">&nbsp;</th>
					<th>
						File Name
						<span className="responsiveSubHeader">
							<br />
							Chapter/Page
						</span>
					</th>
					<th className="chapterPageHeader">Chapter/Page</th>
					<th style={{ textAlign: 'center' }}>
						Self-Serve
						<br />
						Sample
					</th>
					<th>&nbsp;</th>
				</tr>
			</thead>
			<DelayedDroppableWrapper droppableId="lecture-slide-decks" type="LectureSlideDeck">
				{(droppableProvided) => (
					<tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
						{files.length === 0 ? (
							<tr>
								<td colSpan={5} className="emptyState">
									No files uploaded yet.
								</td>
							</tr>
						) : (
							files.map((file, idx) => (
								<Draggable
									key={file.id}
									draggableId={file.id}
									index={idx}
									isDragDisabled={!!file.upload}>
									{(draggableProvided, snapshot) => (
										<FileRow
											file={file}
											courseId={courseId}
											selectOptions={selectOptions}
											defaultExpanded={expandedIds?.has(file.id) ?? false}
											onChange={onChange}
											onRemove={onRemove}
											onReplace={onReplace}
											draggableProvided={draggableProvided}
											isDragging={snapshot.isDragging}
										/>
									)}
								</Draggable>
							))
						)}
						{droppableProvided.placeholder}
					</tbody>
				)}
			</DelayedDroppableWrapper>
		</table>
	</DragDropContext>
);

export default FilesTable;
